import React, { useCallback, useMemo, useState } from 'react'
import { ColDef, RowDragEndEvent } from 'ag-grid-enterprise'
import { useTranslation } from 'react-i18next'
import { Empty } from 'antd'
import { Company, CompanyAcquisition } from '../../../types/company/Company'
import AquisitionsModal from './AquisitionsModal'
import { useGridFormatters } from '../../../shared/hooks/useGridFormatters'
import { parseDate, now } from '../../../config/dayjsConfig'
import CustomAgGrid from '../../../shared/components/custom/Grid/CustomAgGrid'
import SubsidiariesActionsCellRenderer, { SubsidiariesActionsContext } from './SubsidiariesActionsCellRenderer'
import { useCtxSubsidiaries as useSubsidiaries } from '../queries/useSubsidiaries'
import { useUpdateSubsidiaryAcquisitions } from '../queries/useUpdateSubsidiaryAcquisitions'
import { useUpdateSubsidiaryOrders } from '../queries/useUpdateSubsidiaryOrders'
import { useDeleteSubsidiary } from '../queries/useDeleteSubsidiary'
import { Loading } from '../../../components/Misc/Loading'
import RequestError from '../../../shared/components/RequestError'
import { useContextParameters } from '../../../services/api/hooks/useContextParameters'

interface SubsidiariesTableProps {
  groupId?: string
}

const getCurrentAcquisitionValue = (acquisitions?: CompanyAcquisition[]): number => {
  if (!acquisitions || acquisitions.length < 1) return 1

  const past = acquisitions.filter(a => {
    const d = parseDate(a.date)
    return d && !d.isAfter(now())
  })

  if (past.length === 0) return 1

  const current = past.reduce((prev, curr) => {
    const currDate = parseDate(curr.date)
    const prevDate = parseDate(prev.date)
    if (!currDate || !prevDate) return prev
    return currDate.isAfter(prevDate) ? curr : prev
  })

  return current.percentage
}

export const SubsidiariesTable: React.FC<SubsidiariesTableProps> = ({ groupId }) => {
  const { t } = useTranslation()
  const formatters = useGridFormatters()
  const { companyId } = useContextParameters()

  const { data: subsidiaries = [], isLoading, isFetching, error, refetch } = useSubsidiaries()
  const { mutate: deleteSubsidiary } = useDeleteSubsidiary()
  const { mutateAsync: updateAcquisitions } = useUpdateSubsidiaryAcquisitions()
  const { mutate: updateOrders } = useUpdateSubsidiaryOrders()

  const [subsidiaryEdit, setSubsidiaryEdit] = useState<Company>()
  const [open, setOpen] = useState(false)

  const onUpdate = async (values: Company) => {
    try {
      await updateAcquisitions(values)
      setOpen(false)
    } catch {
      // modal stays open; error is surfaced via mutation state
    }
  }

  const onEdit = useCallback((company: Company) => {
    setSubsidiaryEdit(company)
    setOpen(true)
  }, [])

  const onDelete = useCallback(
    (company: Company) => {
      deleteSubsidiary(company.id)
    },
    [deleteSubsidiary]
  )

  const onRowDragEnd = useCallback(
    (event: RowDragEndEvent<Company>) => {
      const newList: Company[] = []
      event.api.forEachNode(node => {
        if (node.data) newList.push(node.data)
      })
      const orders = newList.map((row, index) => ({
        companyId,
        subsidiaryId: row.id,
        order: index
      }))
      updateOrders({ orders, newList })
    },
    [updateOrders, companyId]
  )

  const colDefs = useMemo<ColDef<Company>[]>(
    () => [
      {
        colId: 'drag',
        rowDrag: true,
        width: 30,
        maxWidth: 30,
        resizable: false,
        sortable: false,
        suppressHeaderMenuButton: true
      },
      {
        field: 'id',
        headerName: t('global:vatId'),
        flex: 1
      },
      {
        field: 'name',
        headerName: t('global:name'),
        flex: 2
      },
      {
        field: 'acquisitions',
        headerName: t('global:percentage'),
        flex: 1,
        type: 'percent',
        valueGetter: params => getCurrentAcquisitionValue(params.data?.acquisitions),
        valueFormatter: params => formatters.percentage(2)({ value: params.value })
      },
      {
        colId: 'actions',
        headerName: t('global:actions'),
        cellRenderer: SubsidiariesActionsCellRenderer,
        sortable: false,
        suppressHeaderMenuButton: true,
        width: 70,
        maxWidth: 90
      }
    ],
    [t, formatters]
  )

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      filter: false,
      resizable: false
    }),
    []
  )

  const context = useMemo<SubsidiariesActionsContext>(() => ({ onEdit, onDelete }), [onEdit, onDelete])

  if (isLoading) return <Loading />

  if (error) return <RequestError error={error} refetch={refetch} />

  if (subsidiaries.length === 0) return <Empty />

  return (
    <>
      <AquisitionsModal
        groupId={groupId}
        company={subsidiaryEdit}
        open={open}
        onUpdate={onUpdate}
        onCancel={() => setOpen(false)}
      />
      <div data-cy="subsidiaries-grid" style={{ height: 'calc(100vh - 260px)', minHeight: 300 }}>
        <CustomAgGrid
          rowData={subsidiaries}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          getRowId={params => String(params.data.id)}
          loading={isFetching}
          rowDragManaged
          onRowDragEnd={onRowDragEnd}
          context={context}
        />
      </div>
    </>
  )
}
